/**
 * The boundary between a weapon and the thing it is pointed at.
 *
 * ## Why this is an interface and not an import
 *
 * A cannon needs to know where a target is, how big it is, and how much damage it has taken. It
 * must not know that the target is a drone flying a racetrack in the gunnery mode, because the
 * moment it does, `aircraft/weapons/` depends on `game/modes/` and the gun can only ever be fired
 * at this milestone's targets. Everything the weapons need is declared here, the modes implement
 * it over their own arrays, and a future multiplayer aircraft becomes a target by satisfying six
 * fields rather than by being special-cased.
 *
 * ## Why damage is a queue rather than a method call
 *
 * Non-negotiable #7: all sim state lives in serializable plain structs and player control goes
 * through a command layer. If a projectile called `target.damage(12)` the moment it arrived, the
 * authoritative fact "this target lost 12 hit points at this tick" would exist only as a side
 * effect part-way through somebody's update, which is precisely the shape that cannot be
 * replayed, rewound or sent over a wire. Instead a weapon *reports* what it hit into a
 * preallocated queue and the mode applies it, so the tick's damage is a list of plain records —
 * the same list a server would send.
 *
 * The queue is fixed-size and preallocated (non-negotiable #3); reports past the cap are dropped,
 * which at 64 simultaneous impacts in one 120 Hz tick is not a scenario this game produces.
 *
 * `updateFixed` / `updateFrame`: neither. This is a data contract plus one small buffer.
 */

import { Vector3 } from 'three';

/** Everything a weapon needs to know about something it might destroy. */
export interface Engageable {
  /** Stable within a session. Hit reports carry it rather than a reference. */
  readonly id: number;
  /** Dead targets are skipped by seekers and by hit tests. */
  alive: boolean;
  /** Centre of the hit sphere, sim-frame NED [m]. */
  readonly positionNed: Vector3;
  /** Sim-frame NED velocity [m/s]. Seekers need it; so does the gun sight. */
  readonly velocityNed: Vector3;
  /** Hit sphere radius [m]. */
  radiusM: number;
  /**
   * How brightly this thing burns in the infra-red, in arbitrary units where a fighter's tailpipe
   * seen from directly behind is 1.
   *
   * A balloon sets this to 0 and is therefore invisible to a missile and killable only with the
   * gun, which is what makes the balloon course a *gunnery* exercise rather than four seconds of
   * pressing the missile button.
   */
  infraredIntensity: number;
  /** Remaining hit points. The mode owns the arithmetic; weapons only report. */
  hitPoints: number;
}

/**
 * An indexed list of targets.
 *
 * Indexed rather than iterable because `for (const t of list)` allocates an iterator on every
 * call, and this is walked by every projectile in flight on every physics tick.
 */
export interface TargetList {
  readonly length: number;
  at(index: number): Engageable;
}

/** An empty list, so an unarmed or target-free session needs no null checks in the hot path. */
export const NO_TARGETS: TargetList = {
  length: 0,
  at(): Engageable {
    throw new Error('NO_TARGETS has no entries');
  },
};

export type HitKind = 'cannon' | 'missile';

/** One impact, as a plain record. Reused from the queue's pool; never held past the drain. */
export interface HitReport {
  targetId: number;
  kind: HitKind;
  damage: number;
  /** Where it landed, sim-frame NED [m]. */
  positionNed: Vector3;
  /** Unit vector along the impactor's travel, for throwing sparks the right way. */
  directionNed: Vector3;
  /** Impact speed [m/s] — sparks scale with it. */
  speedMps: number;
}

function makeHitReport(): HitReport {
  return {
    targetId: -1,
    kind: 'cannon',
    damage: 0,
    positionNed: new Vector3(),
    directionNed: new Vector3(),
    speedMps: 0,
  };
}

/**
 * A fixed-size ring of hit reports, filled during the tick and drained after it.
 *
 * `push` hands back a record to fill in rather than taking one, so a caller with the numbers
 * already in registers can write them straight into the slot without building an object first.
 * Returns `null` when the buffer is full, which callers may simply ignore — a dropped spark on
 * the frame where sixty-four things were hit at once is not a defect worth a branch elsewhere.
 */
export class HitQueue {
  private readonly reports: HitReport[] = [];
  private countValue = 0;

  constructor(capacity = 64) {
    for (let i = 0; i < capacity; i++) this.reports.push(makeHitReport());
  }

  get length(): number {
    return this.countValue;
  }

  /** A slot to write into, or null when full. */
  push(): HitReport | null {
    if (this.countValue >= this.reports.length) return null;
    return this.reports[this.countValue++];
  }

  at(index: number): HitReport {
    return this.reports[index];
  }

  /** Call after the consumer has read the tick's hits. */
  clear(): void {
    this.countValue = 0;
  }
}
